import { useEffect, useRef } from 'react';
import type { Transaction } from '../types';
import TransactionItem from './TransactionItem';

interface TransactionListProps {
  transactions: Transaction[];
  totalCount?: number;
  loadedCount?: number;
  title?: string;
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onRetryUsd?: () => void;
}

export default function TransactionList({
  transactions,
  totalCount = 0,
  loadedCount = 0,
  title = 'Sold evidence',
  isLoading = false,
  onLoadMore,
  hasMore = false,
  onRetryUsd,
}: TransactionListProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const rawOnlyTransactions = transactions.filter((transaction) => (
    Number.isFinite(transaction.price)
    && !Number.isFinite(transaction.priceUsd)
  ));
  const retryableFxCount = rawOnlyTransactions.filter((transaction) => Boolean(transaction.currency)).length;
  const missingCurrencyCount = rawOnlyTransactions.length - retryableFxCount;

  useEffect(() => {
    if (!hasMore || isLoading || !onLoadMore || !sentinelRef.current) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) onLoadMore();
    }, { rootMargin: '200px' });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoading, onLoadMore]);

  if (!isLoading && transactions.length === 0) {
    return (
      <section className="rounded-[24px] border border-border bg-white px-6 py-14 text-center shadow-sm">
        <h2 className="text-lg font-semibold text-primary">No sold evidence for this market</h2>
        <p className="mt-2 text-sm text-secondary">Try the All markets filter or check again after new marketplace data is indexed.</p>
      </section>
    );
  }

  return (
    <section aria-labelledby="sold-evidence-heading">
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <p className="eyebrow text-cyan">Marketplace records</p>
          <h2 id="sold-evidence-heading" className="mt-2 text-xl font-semibold tracking-[-0.035em] text-primary">{title}</h2>
        </div>
        {totalCount > 0 && <span className="text-xs text-secondary">{loadedCount.toLocaleString()} of {totalCount.toLocaleString()} loaded</span>}
      </div>

      {retryableFxCount > 0 && onRetryUsd && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber/25 bg-amber/10 px-4 py-3" role="status">
          <p className="text-xs text-amber-900">{retryableFxCount} loaded sale(s) retain raw prices while an FX rate is unavailable.</p>
          <button onClick={onRetryUsd} className="rounded-xl border border-amber/30 bg-white px-4 py-2 text-xs font-bold text-amber-900">Retry evidence USD</button>
        </div>
      )}
      {missingCurrencyCount > 0 && (
        <div className="mb-4 rounded-2xl border border-border bg-background-secondary px-4 py-3 text-xs text-secondary" role="status">
          {missingCurrencyCount} loaded sale(s) show raw values only because their source currency metadata is missing.
        </div>
      )}

      <div className="space-y-3">
        {transactions.map((transaction) => <TransactionItem key={transaction.soldId} transaction={transaction} />)}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center gap-2 py-7">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-cyan" />
          <span className="text-sm text-secondary">Loading more sold evidence…</span>
        </div>
      )}
      {hasMore && !isLoading && <div ref={sentinelRef} className="h-2" />}
      {!hasMore && !isLoading && transactions.length > 0 && (
        <p className="py-5 text-center text-[10px] font-bold uppercase tracking-[0.12em] text-secondary-light">All loaded evidence shown</p>
      )}
    </section>
  );
}
